import * as ReactDOM from 'react-dom';
import * as React from 'react';
import { EntityDefinition } from '@src/app/classes/definitions/EntityDefinition';
import { View as ViewComponent } from './components/View/View'; //legacy grid
import { Liquid } from 'liquidjs';
import { mergeStyles } from '@fluentui/react';
import { IInputs, IOutputs } from '.';

export class LegacyGrid implements ComponentFramework.StandardControl<IInputs, IOutputs> {

    private _container: HTMLDivElement;
    private _enableNavigation: boolean;
    private _entityDefinition: ComponentFramework.PropertyHelper.EntityMetadata;
    private _context: ComponentFramework.Context<IInputs>;
    private _liquid: Liquid = new Liquid();
    private _state: ComponentFramework.Dictionary = {};

    /**
     * Used to initialize the control instance. Controls can kick off remote server calls and other initialization actions here.
     * Data-set values are not initialized here, use updateView.
     * @param context The entire property bag available to control via Context Object; It contains values as set up by the customizer mapped to property names defined in the manifest, as well as utility functions.
     * @param notifyOutputChanged A callback method to alert the framework that the control has new outputs ready to be retrieved asynchronously.
     * @param state A piece of data that persists in one session for a single user. Can be set at any point in a controls life cycle by calling 'setControlState' in the Mode interface.
     * @param container If a control is marked control-type='standard', it will receive an empty div element within which it can render its content.
     */
    public init(context: ComponentFramework.Context<IInputs, any>, notifyOutputChanged: () => void, state: ComponentFramework.Dictionary, container: HTMLDivElement): void {
        this._container = container;
        this._context = context;
        this._state = state;
        this._container = container;
        this._enableNavigation = context.parameters.EnableNavigation.raw !== "false";
        if (this._enableNavigation) {
            this._getEntityDefinition();
        }
        if (context.parameters.IsViewPage?.raw === "true") {
            container.classList.add(mergeStyles({
                display: 'flex',
                flexGrow: 1,
                overflow: 'auto'
            }));
        }
    }

    /**
     * Called when any value in the property bag has changed. This includes field values, data-sets, global values such as container height and width, offline status, control metadata values such as label, visible, etc.
     * @param context The entire property bag available to control via Context Object; It contains values as set up by the customizer mapped to names defined in the manifest, as well as utility functions
     */
    public updateView(context: ComponentFramework.Context<IInputs, any>): void {
        this._context = context;
        if (!this._enableNavigation || (this._entityDefinition)) {
            ReactDOM.render(React.createElement(ViewComponent, {
                dataset: <any>context.parameters.Grid,
                resources: context.resources,
                primaryName: this._enableNavigation && this._entityDefinition.PrimaryNameAttribute,
                onOpenDatasetItem: (entityReference) => this._enableNavigation && context.parameters.Grid.openDatasetItem(entityReference),
                //@ts-ignore - part of documentation, not part of types
                pageNumber: context.parameters.Grid.paging.pageNumber,
                enableGroupBy: context.parameters.EnableGroupBy.raw === "true",
                selectableRows: context.parameters.SelectableRows.raw,
                enableFiltering: context.parameters.EnableFiltering.raw !== "false",
                enableSorting: context.parameters.EnableSorting.raw !== "false",
                aggregateObjectArray: JSON.parse(context.parameters.AggregateObjectArray?.raw),
                groupByAttributes: context.parameters.GroupByAttributes?.raw ? context.parameters.GroupByAttributes?.raw.replaceAll(' ', '').split(',') : null,
                aggregationButtonPlacement: context.parameters.AggregationButtonPlacement?.raw,
                getTranslation: this.getTranslation.bind(this),
                factory: context.factory,
                formatting: context.formatting,
                state: this._state,
                mode: context.mode,
                fireEvent: (eventName, object) => {
                    // @ts-ignore - fireEvent is not part of PCF types at the moment
                    context.factory.fireEvent(eventName, object);
                }
            }), this._container);
        }
    }

    /**
     * It is called by the framework prior to a control receiving new data.
     * @returns an object based on nomenclature defined in manifest, expecting object[s] for property marked as "bound" or "output"
     */
    public getOutputs(): IOutputs {
        return {
            DatasetControl: this._context.parameters.Grid
        };
    }

    /**
     * Called when the control is to be removed from the DOM tree. Controls should use this call for cleanup.
     * i.e. cancelling any pending remote calls, removing listeners, etc.
     */
    public destroy(): void {
        if (!this._container) {
            return;
        }
        ReactDOM.unmountComponentAtNode(this._container);
    }

    public getTranslation(string: string, variables: {} = {}): string {
        const localString = this._context.resources.getString(string);
        return Object.keys(variables).length > 0 ? this._liquid.parseAndRenderSync(localString, variables) : localString;
    }
    //legacy grid
    private async _getEntityDefinition() {
        this._entityDefinition = await EntityDefinition.getAsync(this._context.parameters.TargetEntityType.raw);
        this.updateView(this._context);
    }
}
